import React, { useCallback, useEffect, useRef, useState } from 'react'
import './youtubepreview.css'

// Locked-down YouTube preview for the surgeon form.
//
// This is deliberately the same shape as the patient app's player
// (physio_app_web/components/YouTubeVideo/YouTubeVideo.js): a sandboxed iframe
// under a shield that takes every pointer event, so YouTube's own chrome -
// video title, channel link, share, "More videos", the YouTube logo - is never
// reachable. A clinician can still play the video to confirm they pasted the
// right one, which is all this preview is for.

const YT_ORIGIN = 'https://www.youtube-nocookie.com'

const STATE_ENDED = 0
const STATE_PLAYING = 1
const STATE_PAUSED = 2

const buildEmbedUrl = (videoId) => {
  const params = [
    'enablejsapi=1',
    'autoplay=1',
    'controls=0',
    'modestbranding=1',
    'rel=0',
    'fs=0',
    'disablekb=1',
    'iv_load_policy=3',
    'playsinline=1',
  ]
  if (window.location && window.location.origin) {
    params.push('origin=' + encodeURIComponent(window.location.origin))
  }
  return YT_ORIGIN + '/embed/' + videoId + '?' + params.join('&')
}

const YouTubePreview = ({ videoId }) => {
  const [mounted, setMounted] = useState(false)
  const [uiState, setUiState] = useState('idle')
  const [posterFailed, setPosterFailed] = useState(false)
  const [unavailable, setUnavailable] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  const iframeRef = useRef(null)

  const post = useCallback((func) => {
    const frame = iframeRef.current
    if (!frame || !frame.contentWindow) return
    frame.contentWindow.postMessage(
      JSON.stringify({ event: 'command', func: func, args: [] }),
      YT_ORIGIN
    )
  }, [])

  const handleFrameLoad = useCallback(() => {
    const frame = iframeRef.current
    if (!frame || !frame.contentWindow) return
    frame.contentWindow.postMessage(
      JSON.stringify({ event: 'listening', id: videoId, channel: 'widget' }),
      YT_ORIGIN
    )
  }, [videoId])

  // A new link means a new video: drop back to the poster.
  useEffect(() => {
    setMounted(false)
    setUiState('idle')
    setUnavailable(false)
    setPosterFailed(false)
  }, [videoId])

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== YT_ORIGIN) return
      const frame = iframeRef.current
      if (!frame || event.source !== frame.contentWindow) return

      let data = event.data
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data)
        } catch (err) {
          return
        }
      }
      if (!data) return

      if (data.event === 'onError') {
        setUnavailable(true)
        return
      }

      let state = null
      if (data.event === 'onStateChange' && typeof data.info === 'number') {
        state = data.info
      } else if (
        data.event === 'infoDelivery' &&
        data.info &&
        typeof data.info.playerState === 'number'
      ) {
        state = data.info.playerState
      }
      if (state === null) return

      if (state === STATE_ENDED) {
        setMounted(false)
        setUiState('idle')
      } else if (state === STATE_PLAYING) {
        setUiState('playing')
      } else if (state === STATE_PAUSED) {
        setUiState('paused')
      }
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  const toggle = useCallback(() => {
    if (unavailable) return
    if (!mounted) {
      setMounted(true)
      return
    }
    if (uiState === 'playing') {
      post('pauseVideo')
      return
    }
    if (uiState === 'idle') {
      setReloadKey((key) => key + 1)
      return
    }
    post('playVideo')
  }, [unavailable, mounted, uiState, post])

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      toggle()
    }
  }

  if (!videoId) return null

  if (unavailable) {
    return (
      <div className='yt-preview'>
        <div className='yt-preview-message'>
          This video cannot be embedded. It may be private, deleted, or the
          owner may have disabled embedding.
        </div>
      </div>
    )
  }

  return (
    <div className='yt-preview'>
      {mounted && (
        <iframe
          key={reloadKey}
          ref={iframeRef}
          className='yt-preview-frame'
          title='YouTube video preview'
          src={buildEmbedUrl(videoId)}
          onLoad={handleFrameLoad}
          sandbox='allow-scripts allow-same-origin allow-presentation'
          allow='autoplay; encrypted-media'
          referrerPolicy='strict-origin-when-cross-origin'
          frameBorder='0'
          tabIndex={-1}
        />
      )}

      {uiState === 'idle' && !posterFailed && (
        <img
          className='yt-preview-poster'
          src={'https://i.ytimg.com/vi/' + videoId + '/hqdefault.jpg'}
          alt=''
          onError={() => setPosterFailed(true)}
        />
      )}

      <div
        className='yt-preview-shield'
        role='button'
        tabIndex={0}
        aria-label={uiState === 'playing' ? 'Pause video' : 'Play video'}
        onClick={toggle}
        onKeyDown={handleKeyDown}
      >
        {uiState !== 'playing' && (
          <div className='yt-preview-scrim'>
            {/* SVG rather than a CSS border-triangle: the geometry travels
                with the viewBox and cannot be distorted by ambient styles. */}
            <svg
              className='yt-preview-play'
              viewBox='0 0 68 68'
              aria-hidden='true'
              focusable='false'
            >
              <circle
                cx='34'
                cy='34'
                r='32'
                fill='rgba(0, 0, 0, 0.55)'
                stroke='#fff'
                strokeWidth='2.5'
              />
              <path d='M28 21.5 L49 34 L28 46.5 Z' fill='#fff' />
            </svg>
          </div>
        )}
      </div>
    </div>
  )
}

export default YouTubePreview
